import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import api, { formatApiError } from "../lib/api";

/**
 * v4.6.6 — Viva Smart Checkout return page. Viva sends back `s` (order code)
 * and `t` (transaction id); both are only hints, so the backend re-verifies
 * the transaction before the booking is marked paid.
 */
export default function VivaReturn() {
  const [params] = useSearchParams();
  const [state, setState] = useState({ status: "verifying" });

  useEffect(() => {
    const orderCode = params.get("s") || params.get("orderCode") || "";
    const transactionId = params.get("t") || params.get("transactionId") || "";
    if (!orderCode || !transactionId) {
      setState({ status: "error", detail: "Missing payment reference in the return URL." });
      return;
    }
    api.post("/public/viva/verify", { order_code: orderCode, transaction_id: transactionId })
      .then(({ data }) => setState({ status: "ok", booking: data }))
      .catch((e) => setState({
        status: "error",
        detail: formatApiError(e.response?.data?.detail) || e.message,
      }));
  }, [params]);

  return (
    <div className="min-h-screen bg-[#F7F7F5] flex items-center justify-center px-6" data-testid="viva-return-page">
      <div className="bg-white border border-black/10 rounded p-10 max-w-lg w-full">
        {state.status === "verifying" && (
          <div className="flex items-center gap-3" data-testid="viva-verifying">
            <Loader2 size={18} className="animate-spin" />
            <p className="text-sm">Verifying your payment with Viva…</p>
          </div>
        )}
        {state.status === "ok" && (
          <div data-testid="viva-success">
            <CheckCircle2 size={28} className="text-emerald-600" />
            <h1 className="font-display text-3xl mt-4">Payment received</h1>
            <p className="text-sm text-neutral-600 mt-2">
              Reservation <strong>{state.booking?.booking_reference || state.booking?.id}</strong> is
              confirmed. The confirmation email with your rental agreement is on its way.
            </p>
            <Link
              to="/manage-booking"
              className="inline-block mt-6 bg-black text-white text-sm px-5 py-2.5 rounded"
              data-testid="viva-manage-link"
            >
              Manage my booking
            </Link>
          </div>
        )}
        {state.status === "error" && (
          <div data-testid="viva-error">
            <XCircle size={28} className="text-rose-600" />
            <h1 className="font-display text-3xl mt-4">We could not confirm the payment</h1>
            <p className="text-sm text-neutral-600 mt-2">{state.detail}</p>
            <p className="text-xs text-neutral-500 mt-3">
              If you completed the payment, our system will finalise it automatically
              from Viva's notification — no need to pay again.
            </p>
            <Link
              to="/book"
              className="inline-block mt-6 border border-black/20 text-sm px-5 py-2.5 rounded"
              data-testid="viva-back-link"
            >
              Back to booking
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
